import { Component, OnInit, Input, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatAngularModule } from '../../modules/mat-angular.module';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { BlockUIService } from '../../../services/block-ui.service';
import { TemplateCommonService } from '../../components/template-ai-common/services/template-common.service';
import { BarLineComboChartComponent } from '../../charts/bar-line-combo-chart/bar-line-combo-chart.component';
import { PolarChartComponent } from '../../charts/polar-chart/polar-chart.component';
import { CompositeScoreWidgetComponent } from '../../components/composite-score-widget/composite-score-widget.component';
import { CommonSourcesComponent } from '../../components/template-ai-common/common-sources/common-sources.component';
import { CommonPerformanceTableComponent, TableColumn, TableRow } from '../../components/common-performance-table/common-performance-table.component';
import { SentimentTrendsComponent } from '../../components/sentiment-trends/sentiment-trends.component';
import { sentimentAnalysis } from '../../sample-education/sentiment-analysis';

@Component({
  selector: 'app-sentiment-analysis-tab',
  standalone: true,
  imports: [
    CommonModule,
    MatAngularModule,
    NgxChartsModule,
    BarLineComboChartComponent,
    PolarChartComponent,
    CompositeScoreWidgetComponent,
    CommonSourcesComponent,
    CommonPerformanceTableComponent,
    SentimentTrendsComponent
  ],
  templateUrl: './sentiment-analysis-tab.component.html',
  styleUrl: './sentiment-analysis-tab.component.scss'
})
export class SentimentAnalysisTabComponent implements OnInit, OnChanges, OnDestroy {

  @Input() data: any = null;
  @Input() title: string = 'Sentiment Analysis';

  // Legacy inputs for backward compatibility
  @Input() analysisSentiment: any[] = [];
  @Input() brands: string[] = [];

  // Core data properties
  universities: any[] = [];
  sentimentBreakdown: any = null;
  context: any = null;
  metadata: any = null;
  evidenceSources: any[] = [];
  recommendations: any[] = [];

  // Chart data properties
  sentimentComboData: any = null;
  sentimentRadarData: any[] = [];

  // Combo Chart V2 properties
  results: any[] = [];
  lineChart: any[] = [];
  colors: any;
  transform: string = '';
  dims: any;
  xScale: any;
  yScale: any;

  // Responsive view dimensions
  view: [number, number] = [600, 400];

  // Combo Chart Color Schemes - matching ngx-charts structure
  comboBarScheme: any = {
    name: 'singleLightBlue',
    selectable: true,
    group: 'ordinal',
    domain: ['#1976d2']
  };

  lineChartScheme: any = {
    name: 'coolthree',
    selectable: true,
    group: 'ordinal',
    domain: ['#01579b', '#7aa3e5', '#a8385d', '#00bfa5']
  };

  get firstUniversityName(): string {
    return this.getFirstUniversityName();
  }

  // Combo Chart Axis Scaling and Formatting - matching ngx-charts structure
  yLeftAxisScale(min: number, max: number): any {
    return { min: `${min}`, max: `${max}` };
  }

  yRightAxisScale(min: number, max: number): any {
    return { min: `${min}`, max: `${max}` };
  }

  yLeftTickFormat(data: number): string {
    return `${data.toLocaleString()}`;
  }

  yRightTickFormat(data: number): string {
    return `${data}%`;
  }

  // Chart event handlers
  onSelect(event: any): void {
    // console.log('Sentiment chart item selected:', event);
  }

  onActivate(event: any): void {
    // console.log('Sentiment combo chart activated:', event);
  }

  onDeactivate(event: any): void {
    // console.log('Sentiment combo chart deactivated:', event);
  }

  // Legacy data for backward compatibility
  sentimentData: any[] = [];

  // Self-contained data loading properties
  isLoading: boolean = false;
  error: string | null = null;
  hasLoadedData: boolean = false;
  private dataCache: any = null;

  // Table properties
  tableColumns: TableColumn[] = [];
  tableRows: TableRow[] = [];

  // Sentiment Tracking Table properties
  sentimentTrackingTableColumns: TableColumn[] = [];
  sentimentTrackingTableRows: TableRow[] = [];

  loadingSub: any;

  constructor(
    private blockUIService: BlockUIService,
    private templateCommonService: TemplateCommonService
  ) { }


  // Initialize table columns
  private initializeTableColumns(): void {
    this.tableColumns = [
      { key: 'university', header: 'University', className: 'university-name' },
      { key: 'sentiment_score', header: 'Sentiment Score' },
      { key: 'social_media_sentiment', header: 'Social Media' },
      { key: 'student_satisfaction', header: 'Student Satisfaction' },
      { key: 'news_sentiment', header: 'News Sentiment' },
      { key: 'overall_reputation', header: 'Overall Reputation' },
      { key: 'platform_sentiment.twitter', header: 'Twitter' },
      { key: 'platform_sentiment.reddit', header: 'Reddit' },
      { key: 'platform_sentiment.student_reviews', header: 'Student Reviews' },
      { key: 'platform_sentiment.news_articles', header: 'News Articles' }
    ];
  }

  // Initialize sentiment tracking table columns
  private initializeSentimentTrackingTableColumns(): void {
    this.sentimentTrackingTableColumns = [
      { key: 'university', header: 'University', className: 'university-name' },
      { key: 'academic_quality', header: 'Academic Quality' },
      { key: 'student_experience', header: 'Student Experience' },
      { key: 'financial', header: 'Financial' },
      { key: 'career_outcome', header: 'Career Outcome' },
      { key: 'trust_safety', header: 'Trust & Safety' }
    ];
  }

  ngOnInit() {
    this.loadingSub = this.blockUIService.isLoading$.subscribe(status => {
      if (status === true) {
        // Clear data when loading starts
        this.sentimentData = [];
        this.sentimentRadarData = [];
        this.sentimentComboData = null;
      }
    });

    // Initialize table columns
    this.initializeTableColumns();
    this.initializeSentimentTrackingTableColumns();

    // Load default data when component initializes
    this.loadDefaultData();
  }

  ngOnChanges(changes: SimpleChanges) {
    // Handle data input (new structure)
    if (changes['data'] && changes['data'].currentValue) {
      this.processSentimentAnalysisData(changes['data'].currentValue);
    }

    // Handle legacy analysisSentiment input
    if (changes['analysisSentiment'] && changes['analysisSentiment'].currentValue) {
      this.sentimentData = changes['analysisSentiment'].currentValue;
    }
  }

  ngOnDestroy() {
    if (this.loadingSub) {
      this.loadingSub.unsubscribe();
    }
  }

  private processSentimentAnalysisData(data: any): void {
    // Store the raw data for reference
    this.data = data;

    // console.log('SentimentAnalysisTab: Processing data:', data);
    // console.log('SentimentAnalysisTab: evidence_sources:', data.evidence_sources);
    // console.log('SentimentAnalysisTab: recommendations:', data.recommendations);

    // Extract all the rich data from the response
    this.universities = data.universities || [];
    this.sentimentBreakdown = data.composite_score?.value || null;
    this.context = data.context || null;
    this.metadata = data.metadata || null;
    this.evidenceSources = data.evidence_sources || data.references?.sources || [];
    this.recommendations = data.recommendations || [];

    // console.log('SentimentAnalysisTab: Extracted universities:', this.universities);
    // console.log('SentimentAnalysisTab: Universities count:', this.universities.length);
    // console.log('SentimentAnalysisTab: Extracted evidenceSources:', this.evidenceSources);
    // console.log('SentimentAnalysisTab: Extracted recommendations:', this.recommendations);

    // Process chart data
    this.processChartData();

    // Process table data
    this.processTableData();

    // Process sentiment tracking table data
    this.processSentimentTrackingTableData();

    // Process legacy data for backward compatibility
    if (data.data) {
      this.sentimentData = data.data;
    } else if (data.universities) {
      // Transform universities data to legacy format
      this.sentimentData = this.transformUniversitiesToLegacyFormat(data.universities);
    }
  }

  private processTableData(): void {
    // Use performance_table if available, otherwise use universities data
    if (this.data.performance_table && this.data.performance_table.length > 0) {
      this.tableRows = this.data.performance_table;
    } else if (this.universities && this.universities.length > 0) {
      this.tableRows = this.universities;
    } else {
      this.tableRows = [];
    }
  }

  private processSentimentTrackingTableData(): void {
    // Use sentiment_tracking_table if available, otherwise extract from universities data
    if (this.data.sentiment_tracking_table && this.data.sentiment_tracking_table.length > 0) {
      this.sentimentTrackingTableRows = this.data.sentiment_tracking_table;
    } else if (this.universities && this.universities.length > 0) {
      // Extract sentiment tracking data from universities
      this.sentimentTrackingTableRows = this.universities.map(university => ({
        university: university.name,
        is_your_brand: university.is_your_brand || false,
        academic_quality: university.sentiment_tracking?.academic_quality || 0,
        student_experience: university.sentiment_tracking?.student_experience || 0,
        financial: university.sentiment_tracking?.financial || 0,
        career_outcome: university.sentiment_tracking?.career_outcome || 0,
        trust_safety: university.sentiment_tracking?.trust_safety || 0
      }));
    } else {
      this.sentimentTrackingTableRows = [];
    }
  }

  private transformUniversitiesToLegacyFormat(universities: any[]): any[] {
    if (!universities || universities.length === 0) {
      return [];
    }

    return universities.map(university => ({
      name: university.name,
      series: [
        {
          name: "Sentiment Score",
          value: university.sentiment_score
        }
      ]
    }));
  }


  private processChartData(): void {
    // Process combo chart data from universities
    this.processComboChartData();

    // Process combo-chart-v2 data
    this.prepareComboChartV2Data();

    // Process radar chart data
    this.processRadarChartData();
  }

  private processRadarChartData(): void {
    if (this.universities && this.universities.length > 0) {
      // Create radar chart data from universities sentiment metrics
      this.sentimentRadarData = this.universities.map(university => ({
        name: university.name,
        series: [
          {
            name: 'Sentiment Score',
            value: university.sentiment_score || 0
          },
          {
            name: 'Social Media',
            value: university.social_media_sentiment || 0
          },
          {
            name: 'Student Satisfaction',
            value: university.student_satisfaction || 0
          },
          {
            name: 'News Sentiment',
            value: university.news_sentiment || 0
          },
          {
            name: 'Overall Reputation',
            value: university.overall_reputation || 0
          }
        ]
      }));
    } else {
      // Fallback radar chart data - use first university from sample data
      if (this.universities && this.universities.length > 0) {
        const firstUniversity = this.universities[0];
        this.sentimentRadarData = [
          {
            name: firstUniversity.name,
            series: [
              { name: 'Sentiment Score', value: firstUniversity.sentiment_score || 0 },
              { name: 'Social Media', value: firstUniversity.social_media_sentiment || 0 },
              { name: 'Student Satisfaction', value: firstUniversity.student_satisfaction || 0 },
              { name: 'News Sentiment', value: firstUniversity.news_sentiment || 0 },
              { name: 'Overall Reputation', value: firstUniversity.overall_reputation || 0 }
            ]
          }
        ];
      } else {
        // Ultimate fallback
        this.sentimentRadarData = [
          {
            name: 'No Data',
            series: [
              { name: 'Sentiment Score', value: 0 },
              { name: 'Social Media', value: 0 },
              { name: 'Student Satisfaction', value: 0 },
              { name: 'News Sentiment', value: 0 },
              { name: 'Overall Reputation', value: 0 }
            ]
          }
        ];
      }
    }
  }



  getEvidenceTooltip(evidence: any): string {
    if (!evidence) return '';

    let tooltip = '';
    if (evidence.title) tooltip += `Title: ${evidence.title}\n`;
    if (evidence.category) tooltip += `Category: ${evidence.category}\n`;
    if (evidence.confidence && typeof evidence.confidence === 'number') {
      tooltip += `Confidence: ${Math.round(evidence.confidence * 100)}%\n`;
    }
    if (evidence.snippet) tooltip += `Snippet: ${evidence.snippet}\n`;
    if (evidence.url) tooltip += `\nClick to view source`;

    return tooltip.trim();
  }

  getLegacySentimentTooltip(item: any): string {
    if (!item) return '';

    let tooltip = '';
    tooltip += `Name: ${item.name}\n`;
    if (item.series && item.series.length > 0) {
      tooltip += `Sentiment Score: ${item.series[0].value}\n`;
    }
    if (item.url) {
      tooltip += `\nClick to view source`;
    } else {
      tooltip += `\nNo source URL available`;
    }

    return tooltip.trim();
  }

  openSourceLink(url: string): void {
    if (url && url.trim() !== '' && url !== '#') {
      // Open the link in a new tab
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      // Show a message if no URL is available
      // console.log('No source URL available for this item');
    }
  }

  private processComboChartData(): void {
    // Check if we have competitor_analysis data from the API response
    if (this.data && this.data.competitor_analysis) {
      this.sentimentComboData = this.data.competitor_analysis;
    } else if (this.universities && this.universities.length > 0) {
      const xAxisLabels = ['Overall Sentiment', 'Social Media', 'Student Satisfaction', 'News Sentiment', 'Overall Reputation'];

      const series = this.universities.map((university, index) => {
        const data = [
          university.sentiment_score || 0,
          university.social_media_sentiment || 0,
          university.student_satisfaction || 0,
          university.news_sentiment || 0,
          university.overall_reputation || 0
        ];

        return {
          name: university.name,
          data: data,
          type: index === 0 ? 'bar' : 'line',
          color: index === 0 ? '#1976d2' : undefined
        };
      });

      this.sentimentComboData = {
        title: 'University Sentiment Analysis',
        subtitle: 'Sentiment metrics comparison across universities',
        series: series,
        x_axis: xAxisLabels,
        y_axis: 'Score',
        chart_type: 'combo'
      };
    } else {
      // Fallback data if no universities available
      this.sentimentComboData = {
        title: 'University Sentiment Analysis',
        subtitle: 'Sample sentiment metrics comparison',
        series: [
          {
            name: 'Sample University',
            data: [75, 80, 70, 65, 85],
            type: 'bar',
            color: '#1976d2'
          },
          {
            name: 'Competitor 1',
            data: [70, 75, 65, 60, 80],
            type: 'line'
          },
          {
            name: 'Competitor 2',
            data: [80, 85, 75, 70, 90],
            type: 'line'
          }
        ],
        x_axis: ['Overall Sentiment', 'Social Media', 'Student Satisfaction', 'News Sentiment', 'Overall Reputation'],
        y_axis: 'Score',
        chart_type: 'combo'
      };
    }
  }

  // Prepare data for combo-chart-v2 component
  private prepareComboChartV2Data(): void {
    // console.log('🔍 Sentiment: prepareComboChartV2Data called');
    // console.log('🔍 Sentiment: universities:', this.universities);
    // console.log('🔍 Sentiment: universities length:', this.universities?.length);

    if (!this.universities || this.universities.length === 0) {
      // console.log('❌ Sentiment: No universities data, clearing results and lineChart');
      this.results = [];
      this.lineChart = [];
      return;
    }

    const universities = this.universities;
    const youUniversity = this.findYouUniversity(universities);
    const competitorUniversities = this.findCompetitorUniversities(universities);

    // console.log('🎯 Sentiment: You university found:', youUniversity?.name);
    // console.log('📊 Sentiment: Competitor universities:', competitorUniversities.map(u => u.name));

    // Prepare bar chart data (results) - YOU university metrics as bars
    if (youUniversity) {
      // console.log('📊 Sentiment: Preparing results for youUniversity:', youUniversity.name);
      // console.log('📊 Sentiment: YouUniversity data:', youUniversity);
      this.results = [
        { name: 'Overall Sentiment', value: youUniversity.sentiment_score || 0 },
        { name: 'Social Media', value: youUniversity.social_media_sentiment || 0 },
        { name: 'Student Satisfaction', value: youUniversity.student_satisfaction || 0 },
        { name: 'News Sentiment', value: youUniversity.news_sentiment || 0 },
        { name: 'Overall Reputation', value: youUniversity.overall_reputation || 0 }
      ];
      // console.log('📊 Sentiment: Results prepared:', this.results);
    } else {
      // console.log('❌ Sentiment: No youUniversity found, clearing results');
      this.results = [];
    }

    // Prepare line chart data (lineChart) - competitor universities as lines
    if (competitorUniversities.length > 0) {
      // console.log('📈 Sentiment: Preparing lineChart for competitors:', competitorUniversities.length);
      this.lineChart = competitorUniversities.map(university => ({
        name: university.name,
        series: [
          { name: 'Overall Sentiment', value: university.sentiment_score || 0 },
          { name: 'Social Media', value: university.social_media_sentiment || 0 },
          { name: 'Student Satisfaction', value: university.student_satisfaction || 0 },
          { name: 'News Sentiment', value: university.news_sentiment || 0 },
          { name: 'Overall Reputation', value: university.overall_reputation || 0 }
        ]
      }));
      // console.log('📈 Sentiment: LineChart prepared:', this.lineChart);
    } else {
      // console.log('❌ Sentiment: No competitor universities, clearing lineChart');
      this.lineChart = [];
    }

    // Set colors
    this.colors = this.comboBarScheme;

    // console.log('Sentiment: Combo-chart-v2 results prepared:', this.results);
    // console.log('Sentiment: Combo-chart-v2 lineChart prepared:', this.lineChart);
  }

  // Helper methods to find YOU and competitor universities
  private findYouUniversity(universities: any[]): any | null {
    // If there's only one university, treat it as the "you" university
    if (universities.length === 1) {
      // console.log('🎯 Sentiment: Single university detected, treating as "you" university:', universities[0].name);
      return universities[0];
    }

    // First, try to find university with "(YOU)" in the name
    let youUniversity = universities.find(university =>
      university.name && university.name.toLowerCase().includes('(you)')
    );

    // If not found, try to find university with "you" in the name
    if (!youUniversity) {
      youUniversity = universities.find(university =>
        university.name && university.name.toLowerCase().includes('you')
      );
    }

    // If still not found, check for is_your_brand property
    if (!youUniversity) {
      youUniversity = universities.find(university =>
        university.is_your_brand === true
      );
    }

    // If still not found, use the first university as fallback
    if (!youUniversity && universities.length > 0) {
      youUniversity = universities[0];
    }

    return youUniversity || null;
  }

  private findCompetitorUniversities(universities: any[]): any[] {
    const youUniversity = this.findYouUniversity(universities);
    if (!youUniversity) return universities;

    return universities.filter(university =>
      university.name !== youUniversity.name
    );
  }

  private getFirstUniversityName(): string {
    if (this.universities && this.universities.length > 0) {
      const youUniversity = this.findYouUniversity(this.universities);
      if (youUniversity) {
        return youUniversity.name.replace(' (YOU)', '');
      }
      return this.universities[0].name || 'University';
    }
    return 'University';
  }

  // Check if combo chart should be shown
  shouldShowComboChart(): boolean {
    return !!(this.universities && this.universities.length > 0);
  }

  // Get chart subtitle based on data
  getChartSubtitle(): string {
    if (!this.universities || this.universities.length === 0) {
      return 'No data available';
    }

    if (this.universities.length === 1) {
      const university = this.universities[0];
      return `📊 Single University Analysis: ${university.name} - Detailed Sentiment metrics breakdown`;
    } else {
      const youUniversity = this.findYouUniversity(this.universities);
      const competitorCount = this.universities.length - 1;
      if (youUniversity) {
        return `Sentiment comparison: ${youUniversity.name} (bars) vs ${competitorCount} competitor${competitorCount > 1 ? 's' : ''} (lines)`;
      } else {
        return `Sentiment comparison across ${this.universities.length} universities`;
      }
    }
  }

  // Self-contained data loading methods
  private loadDefaultData(): void {
    // Load sample data by default
    this.loadSampleData();
  }

  private loadSampleData(): void {
    // Load sample sentiment analysis data from the imported sample file
    this.processSentimentAnalysisData(sentimentAnalysis);
    this.hasLoadedData = true;
  }

  public loadApiData(forceRefresh: boolean = false): void {
    const currentFilterData = this.templateCommonService.getCurrentFilterData();
    // console.log("currentFilterData", currentFilterData);
    const compareClicked = this.templateCommonService.getCompareClicked();
    const hasFilterData = currentFilterData &&
      currentFilterData.selectedEntities &&
      currentFilterData.selectedEntities.length > 0 &&
      currentFilterData.category &&
      currentFilterData.country;

    if (!hasFilterData) {
      // console.log('No filter data available, using sample data');
      this.loadSampleData();
      return;
    }

    if (!forceRefresh && this.dataCache) {
      // console.log('Using cached sentiment data');
      this.processSentimentAnalysisData(this.dataCache);
      return;
    }

    this.isLoading = true;
    this.error = null;

    this.templateCommonService.loadSentimentTrackingIndex(
      currentFilterData.selectedEntities,
      currentFilterData.category,
      forceRefresh
    ).subscribe({
      next: (transformedData: any) => {
        // console.log('Sentiment data loaded successfully:', transformedData);
        this.dataCache = transformedData;
        this.processSentimentAnalysisData(transformedData);
        this.isLoading = false;
        this.hasLoadedData = true;
      },
      error: (error: any) => {
        console.error('Error loading sentiment data:', error);

        // Extract detailed error message
        let errorMessage = 'Failed to load sentiment data';
        if (error?.error?.message) {
          errorMessage = error.error.message;
        } else if (error?.message) {
          errorMessage = error.message;
        } else if (typeof error === 'string') {
          errorMessage = error;
        }

        this.error = errorMessage;
        this.isLoading = false;
        // Fallback to sample data on error
        this.loadSampleData();
      }
    });
  }

  public refreshData(): void {
    this.loadApiData(true);
  }

}
